import React, { useMemo } from 'react'
import { DatePosition, getPercentXForDate } from '../hooks/useDatePositions'
import { GanttColors, GanttDimensions } from '../constants'
import { CriticalPathLinkData } from './CriticalPathContext'

interface CriticalPathLinesProps {
  datePositions: DatePosition[]
  criticalPathLinks: CriticalPathLinkData[]
  currentRowIndex: number
  onLineClick?: (link: CriticalPathLinkData, event: React.MouseEvent) => void
}

export const CriticalPathLines = React.memo<CriticalPathLinesProps>(
  ({ datePositions, criticalPathLinks, currentRowIndex, onLineClick }) => {
    // Build elements and collect used colors in a single pass
    const { elements, markerDefs } = useMemo(() => {
      const result: React.ReactNode[] = []
      const usedColors = new Set<string>()

      criticalPathLinks.forEach((link, linkIndex) => {
        const handleClick = onLineClick
          ? (e: React.MouseEvent) => onLineClick(link, e)
          : undefined
        const lineColor = link.color || GanttColors.CRITICAL_PATH_LINE
        usedColors.add(lineColor)
        const markerId = `critical-path-arrow-${currentRowIndex}-${lineColor.replace('#', '')}`
        const linkElements = generateCriticalPathElements(
          link,
          linkIndex,
          datePositions,
          currentRowIndex,
          markerId,
          handleClick,
          lineColor
        )
        result.push(...linkElements)
      })

      // Build marker definitions for each unique color
      const markers = Array.from(usedColors).map(color => (
        <marker
          key={color}
          id={`critical-path-arrow-${currentRowIndex}-${color.replace('#', '')}`}
          viewBox="0 0 10 10"
          refX="8"
          refY="5"
          markerWidth="4"
          markerHeight="4"
          orient="auto-start-reverse"
        >
          <path
            d="M 0 0 L 8 5 L 0 10"
            fill="none"
            stroke={color}
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </marker>
      ))

      return { elements: result, markerDefs: markers }
    }, [datePositions, criticalPathLinks, currentRowIndex, onLineClick])

    if (currentRowIndex < 0) return null

    return (
      <g className="critical-path-lines" strokeLinecap="round">
        <defs>{markerDefs}</defs>
        {elements}
      </g>
    )
  }
)

CriticalPathLines.displayName = 'CriticalPathLines'

// ============================================================================
// Types
// ============================================================================

type RowRole = 'source' | 'intermediate' | 'target'

// ============================================================================
// Element Generation
// ============================================================================

function generateCriticalPathElements(
  link: CriticalPathLinkData,
  linkIndex: number,
  datePositions: DatePosition[],
  currentRowIndex: number,
  markerId: string,
  onClick?: (e: React.MouseEvent) => void,
  lineColor: string = GanttColors.CRITICAL_PATH_LINE
): React.ReactNode[] {
  const { fromRowIndex, toRowIndex } = link
  const elements: React.ReactNode[] = []

  const minRow = Math.min(fromRowIndex, toRowIndex)
  const maxRow = Math.max(fromRowIndex, toRowIndex)
  if (currentRowIndex < minRow || currentRowIndex > maxRow) return elements

  const isFromAboveTo = fromRowIndex < toRowIndex
  const rowRole = getRowRole(currentRowIndex, fromRowIndex, toRowIndex)
  const keyPrefix = `critical-path-${linkIndex}`

  const { CRITICAL_PATH_LINE_WIDTH, CRITICAL_PATH_CIRCLE_RADIUS } =
    GanttDimensions

  // Y positions: all within row bounds (0%-100%) to avoid overflow being
  // clipped by adjacent rows' backgrounds (ag-grid ::before pseudo-elements)
  const barTopY = '10%'
  const barBottomY = '90%'
  const topEdgeY = '0%'
  const bottomEdgeY = '100%'

  // For source: exit from bottom if target is below, top if above
  const sourceExitY = isFromAboveTo ? barBottomY : barTopY
  // For target: arrow tip slightly inside the bar for better visual appearance
  const targetEntryY = isFromAboveTo ? '20%' : '80%'

  switch (rowRole) {
    case 'source': {
      const fromX = getPercentXForDate(datePositions, link.fromDate, 'center')
      if (fromX !== null) {
        elements.push(
          <g
            key={`${keyPrefix}-source`}
            onClick={onClick}
            style={{ cursor: onClick ? 'pointer' : undefined }}
          >
            {/* Starting circle at fromDate position, at bar edge */}
            <circle
              cx={`${fromX}%`}
              cy={sourceExitY}
              r={CRITICAL_PATH_CIRCLE_RADIUS}
              fill={lineColor}
            />
            {/* Vertical line from bar edge to row boundary */}
            <line
              x1={`${fromX}%`}
              y1={sourceExitY}
              x2={`${fromX}%`}
              y2={isFromAboveTo ? bottomEdgeY : topEdgeY}
              stroke={lineColor}
              strokeWidth={CRITICAL_PATH_LINE_WIDTH}
            />
            {/* Transparent hit area */}
            <line
              x1={`${fromX}%`}
              y1={sourceExitY}
              x2={`${fromX}%`}
              y2={isFromAboveTo ? bottomEdgeY : topEdgeY}
              stroke="transparent"
              strokeWidth={12}
              style={{ pointerEvents: 'stroke' }}
            />
          </g>
        )
      }
      break
    }

    case 'intermediate': {
      // Vertical line passing through the entire row
      const fromX = getPercentXForDate(datePositions, link.fromDate, 'center')
      if (fromX !== null) {
        elements.push(
          <g
            key={`${keyPrefix}-intermediate`}
            onClick={onClick}
            style={{ cursor: onClick ? 'pointer' : undefined }}
          >
            <line
              x1={`${fromX}%`}
              y1={topEdgeY}
              x2={`${fromX}%`}
              y2={bottomEdgeY}
              stroke={lineColor}
              strokeWidth={CRITICAL_PATH_LINE_WIDTH}
            />
            {/* Transparent hit area */}
            <line
              x1={`${fromX}%`}
              y1={topEdgeY}
              x2={`${fromX}%`}
              y2={bottomEdgeY}
              stroke="transparent"
              strokeWidth={12}
              style={{ pointerEvents: 'stroke' }}
            />
          </g>
        )
      }
      break
    }

    case 'target': {
      const fromX = getPercentXForDate(datePositions, link.fromDate, 'center')
      const toX = getPercentXForDate(datePositions, link.toDate, 'center')

      if (fromX !== null && toX !== null) {
        const entryY = isFromAboveTo ? topEdgeY : bottomEdgeY
        // Turn level: horizontal segment runs between the two vertical segments
        // Positioned inside the row with enough margin from edge to be visible
        const turnY = isFromAboveTo ? '8%' : '92%'

        elements.push(
          <g
            key={`${keyPrefix}-target`}
            onClick={onClick}
            style={{ cursor: onClick ? 'pointer' : undefined }}
          >
            {/* 1. Vertical from row edge to turn level (at fromX) */}
            <line
              x1={`${fromX}%`}
              y1={entryY}
              x2={`${fromX}%`}
              y2={turnY}
              stroke={lineColor}
              strokeWidth={CRITICAL_PATH_LINE_WIDTH}
            />
            {/* 2. Horizontal from fromX to toX (at turn level) */}
            <line
              x1={`${fromX}%`}
              y1={turnY}
              x2={`${toX}%`}
              y2={turnY}
              stroke={lineColor}
              strokeWidth={CRITICAL_PATH_LINE_WIDTH}
            />
            {/* 3. Vertical from turn level to bar entry with arrow (at toX) */}
            <line
              x1={`${toX}%`}
              y1={turnY}
              x2={`${toX}%`}
              y2={targetEntryY}
              stroke={lineColor}
              strokeWidth={CRITICAL_PATH_LINE_WIDTH}
              markerEnd={`url(#${markerId})`}
            />
            {/* Transparent hit areas */}
            <line
              x1={`${fromX}%`}
              y1={entryY}
              x2={`${fromX}%`}
              y2={turnY}
              stroke="transparent"
              strokeWidth={12}
              style={{ pointerEvents: 'stroke' }}
            />
            <line
              x1={`${fromX}%`}
              y1={turnY}
              x2={`${toX}%`}
              y2={turnY}
              stroke="transparent"
              strokeWidth={12}
              style={{ pointerEvents: 'stroke' }}
            />
            <line
              x1={`${toX}%`}
              y1={turnY}
              x2={`${toX}%`}
              y2={targetEntryY}
              stroke="transparent"
              strokeWidth={12}
              style={{ pointerEvents: 'stroke' }}
            />
          </g>
        )
      }
      break
    }
  }

  return elements
}

// ============================================================================
// Utility
// ============================================================================

function getRowRole(
  currentRowIndex: number,
  fromRowIndex: number,
  toRowIndex: number
): RowRole {
  if (currentRowIndex === fromRowIndex) return 'source'
  if (currentRowIndex === toRowIndex) return 'target'
  return 'intermediate'
}
